import { useEffect, useState } from "react";
import { Button, Input, toast } from "@/components/kit";
import type { DeadStockSettingsResponseDTO } from "@/modules/reports/domain/contracts";

const LOOKBACK_PRESETS = [30, 60, 90] as const;
const MIN_DAYS_PRESETS = [60, 90, 180] as const;

type DeadStockSettingsPanelProps = {
  settings?: DeadStockSettingsResponseDTO;
  isLoading?: boolean;
  isSaving?: boolean;
  onSave: (input: { lookbackDays: number; minDays: number }) => Promise<void>;
};

export function DeadStockSettingsPanel({
  settings,
  isLoading,
  isSaving,
  onSave,
}: DeadStockSettingsPanelProps) {
  const [lookbackDays, setLookbackDays] = useState(60);
  const [minDays, setMinDays] = useState(90);

  useEffect(() => {
    if (!settings) return;
    setLookbackDays(settings.lookbackDays);
    setMinDays(settings.minDays);
  }, [settings]);

  if (isLoading && !settings) {
    return null;
  }

  const dirty =
    settings != null &&
    (lookbackDays !== settings.lookbackDays || minDays !== settings.minDays);

  return (
    <section className="brand-card flex flex-col gap-3 p-4 sm:p-5">
      <div>
        <h2 className="font-display text-lg">ຕັ້ງຄ່າສິນຄ້າຄ້າງ</h2>
        <p className="text-muted-foreground text-sm">
          lookback = ຊ່ວງທີ່ນັບຍອດຂາຍ · lot ເກົ່າ = ອາຍຸ lot ຂັ້ນຕ່ຳສຳລັບແນະນຳ
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-sm" htmlFor="dead-lookback">
            Lookback (ວັນ)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {LOOKBACK_PRESETS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setLookbackDays(days)}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${
                  lookbackDays === days
                    ? "bg-secondary/20 font-medium text-foreground"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
              >
                {days}d
              </button>
            ))}
          </div>
          <Input
            id="dead-lookback"
            type="number"
            min={1}
            max={3650}
            value={lookbackDays}
            onChange={(e) => setLookbackDays(Number(e.target.value) || 1)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium text-sm" htmlFor="dead-min-days">
            Lot ເກົ່າ (ວັນ)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {MIN_DAYS_PRESETS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setMinDays(days)}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${
                  minDays === days
                    ? "bg-secondary/20 font-medium text-foreground"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
              >
                ≥{days}d
              </button>
            ))}
          </div>
          <Input
            id="dead-min-days"
            type="number"
            min={1}
            max={3650}
            value={minDays}
            onChange={(e) => setMinDays(Number(e.target.value) || 1)}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          className="rounded-full"
          disabled={!dirty || isSaving}
          onClick={async () => {
            try {
              await onSave({ lookbackDays, minDays });
              toast.success("ບັນທຶກຕັ້ງຄ່າສຳເລັດ");
            } catch (e) {
              toast.error("ບັນທຶກຕັ້ງຄ່າບໍ່ສຳເລັດ", {
                description: e instanceof Error ? e.message : undefined,
              });
            }
          }}
        >
          {isSaving ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຕັ້ງຄ່າ"}
        </Button>
      </div>
    </section>
  );
}
